import { useState, useEffect } from 'react';

interface UseDelayedLoadingOptions {
  delay?: number; // in milliseconds
}

/**
 * Custom hook that delays showing a loading state to prevent flickering
 * when operations complete quickly.
 *
 * @param isLoading - The current loading state
 * @param options - Configuration options
 * @returns boolean - Whether to show the delayed loading state
 *
 * @example
 * const showDelayedLoading = useDelayedLoading(loading, { delay: 300 });
 */
export const useDelayedLoading = (isLoading: boolean, options: UseDelayedLoadingOptions = {}) => {
  const { delay = 300 } = options;
  const [showDelayedLoading, setShowDelayedLoading] = useState(false);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (isLoading) {
      // Start the delay timer
      timeoutId = setTimeout(() => {
        setShowDelayedLoading(true);
      }, delay);
    } else {
      // Reset immediately when loading stops
      setShowDelayedLoading(false);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isLoading, delay]);

  return showDelayedLoading;
};
